import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, CheckCircle2 } from 'lucide-react';

const Home = () => {
  const services = [
    {
      title: "Erhvervsrådgivning",
      description: "Få overblik over din virksomheds økonomi og optimér overskuddet.",
      image: "https://assets.auxodev.dk/assets/3ae65674-a84a-41bf-9a95-918b20317861/c503224e-9b9f-4bd2-b09d-16509b301bea.jpg",
      link: "/erhvervsraadgivning"
    },
    {
      title: "Boligrådgivning",
      description: "Opnå den bedst mulige finansiering af din bolig.",
      image: "https://assets.auxodev.dk/assets/3ae65674-a84a-41bf-9a95-918b20317861/f2252908-a039-4169-8db7-70967c6c0b21.jpg",
      link: "/bolig-raadgivning"
    },
    {
      title: "Pensionsrådgivning",
      description: "Sikr dig en tryg alderdom med den rette pensionsstrategi.",
      image: "https://assets.auxodev.dk/assets/3ae65674-a84a-41bf-9a95-918b20317861/0a2e3d27-41a6-4357-a1e9-a1baa3e477f3.jpg",
      link: "/pensionsraadgivning"
    },
    {
      title: "Investeringsrådgivning",
      description: "Minimér dine omkostninger og få mest muligt ud af din formue.",
      image: "https://assets.auxodev.dk/assets/3ae65674-a84a-41bf-9a95-918b20317861/a1e8e196-9261-419d-8e64-7d095055e100.jpg",
      link: "/investering"
    },
    {
      title: "Privatøkonomisk rådgivning",
      description: "Optimér dine muligheder og skab overblik over hele din økonomi.",
      image: "https://assets.auxodev.dk/assets/3ae65674-a84a-41bf-9a95-918b20317861/f6006dbe-e0f8-4f6c-8e20-06f5fc8dbb2d.jpg",
      link: "/oekonomisk-raadgivning"
    }
  ];

  const blogPosts = [
    {
      title: "Er det nu du skal omlægge dit lån?",
      image: "https://assets.auxodev.dk/assets/3ae65674-a84a-41bf-9a95-918b20317861/94a2ee4c-e633-494c-8da9-5fa6095aa48c.jpg",
      link: "/er-det-nu-du-skal-omlaegge-dit-laan"
    },
    {
      title: "Har du indbetalt for meget til PENSION?",
      image: "https://assets.auxodev.dk/assets/3ae65674-a84a-41bf-9a95-918b20317861/22186a1b-ae89-4de4-97e8-3385c0d37efe.jpg",
      link: "/har-du-indbetalt-for-meget-til-pension-i-2018"
    },
    {
      title: "TJEN PENGE PÅ DIN RESTSKAT!",
      image: "https://assets.auxodev.dk/assets/3ae65674-a84a-41bf-9a95-918b20317861/fa89a9d1-bb43-4751-ad20-103c2021f912.jpg",
      link: "/tjen-penge-paa-din-restskat"
    }
  ];

  return (
    <div>
      <Helmet>
        <title>Uvildig økonomisk rådgivning | Finansoptimering</title>
        <meta name="description" content="Finansoptimering: Her får du uvildig økonomisk rådgivning. Vi rådgiver om optimering af hele din økonomi både som privat og virksomhed. Se mere her" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-slate-900 py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-30">
          <img 
            src="https://assets.auxodev.dk/assets/3ae65674-a84a-41bf-9a95-918b20317861/f6006dbe-e0f8-4f6c-8e20-06f5fc8dbb2d.jpg" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight break-words">
              Få mere ud af din <span className="text-[#b60f00]">økonomi</span> med uvildig rådgivning
            </h1>
            <p className="text-xl text-slate-300 mb-10 leading-relaxed max-w-2xl">
              Vi hjælper dig med at optimere din formue, minimere dine omkostninger og sikre dig den bedste finansielle fremtid – helt uden skjulte dagsordener.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/kontakt-os" 
                className="inline-flex items-center justify-center bg-[#b60f00] text-white px-8 py-4 rounded-xl text-lg font-bold hover:bg-[#a00d00] transition-all shadow-lg hover:shadow-red-900/20 active:scale-95"
              >
                Book gratis møde
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <Link 
                to="/oekonomisk-raadgivning" 
                className="inline-flex items-center justify-center bg-white/10 backdrop-blur-sm text-white border border-white/20 px-8 py-4 rounded-xl text-lg font-bold hover:bg-white/20 transition-all active:scale-95"
              >
                Se vores ydelser
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-slate-50 py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
            {services.map((service, index) => (
              <Link 
                key={index} 
                to={service.link}
                className={`group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 flex flex-col max-h-[350px] ${
                  index < 3 ? 'lg:col-span-2' : 'lg:col-span-3'
                } ${index === services.length - 1 ? 'md:col-span-2' : ''}`}
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 flex-grow flex flex-col">
                  <h3 className="text-[1.2rem] font-bold mb-3 text-slate-900 group-hover:text-[#b60f00] transition-colors break-words hyphens-auto">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-grow">
                    {service.description}
                  </p>
                  <div className="flex items-center text-[#b60f00] font-semibold text-sm">
                    Læs mere <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16">
            <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-8 leading-tight break-words">
              Uvildig økonomisk rådgivning
            </h1>
            <div className="prose prose-lg text-slate-600 mx-auto">
              <p>
                Hos Finansoptimering fokuserer vi på at skabe den bedste sammensætning af din økonomi. Som uvildige rådgivere sælger vi ikke produkter, men optimerer din økonomi gennem løsninger, der er rigtige for dig.
              </p>
              <p>
                Vi hjælper både private og virksomheder med alt fra investering til boligøkonomi. Book et gratis og uforpligtende møde, så vi kan se på dine muligheder.
              </p>
            </div>
          </div>

          {/* YouTube Video Section */}
          <div className="bg-slate-900 rounded-3xl overflow-hidden shadow-2xl relative mb-20">
            <div className="aspect-video">
              <iframe 
                className="w-full h-full"
                src="https://www.youtube.com/embed/p0nEUzGEaoY?si=YQaaRFY5UGOS_M_w" 
                title="YouTube video player" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                referrerPolicy="strict-origin-when-cross-origin" 
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>

        {/* CTA Section - Full Width */}
        <div className="bg-red-50 py-20 text-center shadow-inner border-y border-red-100 mb-20">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Book et gratis møde</h2>
            <p className="text-slate-600 mb-10 text-lg max-w-2xl mx-auto">
              Find ud af, hvad uvildig økonomisk rådgivning kan gøre for dig. Book et møde online eller fysisk, når det passer dig.
            </p>
            <Link 
              to="/kontakt-os" 
              className="inline-flex items-center bg-[#b60f00] text-white px-10 py-5 rounded-2xl text-lg font-bold hover:bg-[#a00d00] transition-all shadow-lg active:scale-95"
            >
              Book gratis møde nu
            </Link>
          </div>
        </div>

        <div className="container mx-auto px-4 max-w-4xl">
          {/* Two Column Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-slate-900 border-l-4 border-[#b60f00] pl-4">Privatøkonomi</h3>
              <p className="text-slate-600">Opnå store besparelser på dine pensioner, lån og opsparing:</p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 size={20} className="text-[#b60f00] mr-3 shrink-0 mt-1" />
                  <span className="font-medium">Optimering af pensionsopsparing</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 size={20} className="text-[#b60f00] mr-3 shrink-0 mt-1" />
                  <span className="font-medium">Gennemgang af realkreditlån</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 size={20} className="text-[#b60f00] mr-3 shrink-0 mt-1" />
                  <span className="font-medium">Minimering af investeringsomkostninger</span>
                </li>
              </ul>
              <Link to="/oekonomisk-raadgivning" className="inline-block text-[#b60f00] font-bold hover:underline">Læs mere her</Link>
            </div>

            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-slate-900 border-l-4 border-[#b60f00] pl-4">Erhvervsrådgivning</h3>
              <p className="text-slate-600">Optimér overskuddet og styrk samspillet mellem virksomhed og privatøkonomi:</p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 size={20} className="text-[#b60f00] mr-3 shrink-0 mt-1" />
                  <span className="font-medium">Optimal fordeling af løn og udbytte</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 size={20} className="text-[#b60f00] mr-3 shrink-0 mt-1" />
                  <span className="font-medium">Effektiv opsparing i virksomheden</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 size={20} className="text-[#b60f00] mr-3 shrink-0 mt-1" />
                  <span className="font-medium">Optimering af erhvervsfinansiering</span>
                </li>
              </ul>
              <Link to="/erhvervsraadgivning" className="inline-block text-[#b60f00] font-bold hover:underline">Læs mere her</Link>
            </div>
          </div>

          {/* Blog Section */}
          <div className="mb-20">
            <div className="flex justify-between items-end mb-10">
              <h2 className="text-3xl font-bold text-slate-900">Nyheder på bloggen</h2>
              <Link to="/blog" className="text-[#b60f00] font-semibold hover:underline flex items-center">
                Se alle indlæg <ArrowRight size={16} className="ml-1" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <Link key={index} to={post.link} className="group">
                  <div className="aspect-square rounded-2xl overflow-hidden mb-4 shadow-sm group-hover:shadow-md transition-all">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <h4 className="font-bold text-slate-900 group-hover:text-[#b60f00] transition-colors leading-snug">
                    {post.title}
                  </h4>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
